import {
	E2EWalletAdapter as UpstreamE2eWalletAdapter,
	E2EWalletAdapterConfig,
} from '@jet-lab/e2e-react-adapter';
import {
	isVersionedTransaction,
	WalletNotConnectedError,
} from '@solana/wallet-adapter-base';
import {
	Transaction,
	TransactionVersion,
	VersionedTransaction,
} from '@solana/web3.js';
import nacl from 'tweetnacl';

/**
 * Dev-only signing wallet for Playwright runs (`?e2eWallet=true`).
 *
 * The upstream adapter only signs legacy transactions and has no
 * `signMessage`, so Swift (signed-message) orders fail before any network
 * call. This subclass signs versioned transactions and messages with the same
 * keypair, and routes every signing method through `rejectNext()`.
 */
export class E2eWalletAdapter extends UpstreamE2eWalletAdapter {
	readonly supportedTransactionVersions: ReadonlySet<TransactionVersion> =
		new Set(['legacy', 0]);

	constructor(config?: E2EWalletAdapterConfig) {
		super(config);
	}

	// Upstream builds _underlyingWallet in the constructor and never clears it,
	// so `connected` (which tracks _publicKey, cleared on disconnect) is the
	// only signal that says whether signing is allowed.
	private get keypair() {
		if (!this.connected) throw new WalletNotConnectedError();
		return this._underlyingWallet;
	}

	async signTransaction<T extends Transaction | VersionedTransaction>(
		transaction: T
	): Promise<T> {
		this._checkForReject();
		const keypair = this.keypair;

		if (isVersionedTransaction(transaction)) {
			transaction.sign([keypair]);
		} else {
			// partialSign, not sign: the legacy sign() replaces the signature list
			// and would drop a cosigner that already signed.
			transaction.partialSign(keypair);
		}

		return transaction;
	}

	async signAllTransactions<T extends Transaction | VersionedTransaction>(
		transactions: T[]
	): Promise<T[]> {
		this._checkForReject();
		const keypair = this.keypair;

		for (const transaction of transactions) {
			if (isVersionedTransaction(transaction)) {
				transaction.sign([keypair]);
			} else {
				transaction.partialSign(keypair);
			}
		}

		return transactions;
	}

	async signMessage(message: Uint8Array): Promise<Uint8Array> {
		this._checkForReject();
		return nacl.sign.detached(message, this.keypair.secretKey);
	}
}
